import { ForwardedRef, forwardRef, useCallback, useMemo } from "react";
import Select, {
  SelectInstance,
  GroupBase,
  createFilter,
  OptionProps,
  components as baseComponents,
} from "react-select";
import { StateManagerProps } from "react-select/dist/declarations/src/useStateManager";
import { Box, Flex } from "@chakra-ui/react";
import {
  LazyQueryResultTuple,
  OperationVariables,
  QueryHookOptions,
  useLazyQuery,
} from "@apollo/client";
import useSelectStyles, { SelectThemingProps } from "utils/useSelectStyles";
import { useCurrentBrandId } from "utils/currentUserHooks";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import {
  SelectSkuMenuValueQuery,
  SelectSkuMenuValueQueryVariables,
  SkuFilter,
} from "__generated__/gql/graphql";
import Link from "components/Shared/Link";
import { PRODUCTS_INDEX } from "utils/links";
import useIdValue, {
  IdValueSelectProps,
} from "components/Shared/Select/useIdValue";
import useFetchOptions from "components/Shared/Select/useFetchOptions";
import ArchivedBadge from "components/Shared/ArchivedBadge";
import { useOptionalCombineFilters } from "utils/useCombineFilters";

// Redecalare forwardRef
// From: https://fettblog.eu/typescript-react-generic-forward-refs/#option-3%3A-augment-forwardref
declare module "react" {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  function forwardRef<T, P = unknown>(
    render: (props: P, ref: React.Ref<T>) => React.ReactElement | null,
  ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

export const skuFragment = gql(/* GraphQL */ `
  fragment SelectSkuMenuSku on Sku {
    id
    name
    fullNameShort
    productLineId
    internalSkuId
    isArchived
  }
`);

export const valueFragment = gql(/* GraphQL */ `
  fragment SelectSkuMenuSkuValue on Sku {
    id
    valueName: fullNameShort
    ...SelectSkuMenuSku
  }
`);

const optionsQuery = gql(/* GraphQL */ `
  query SelectSkuMenuOptions($brandId: UUID!, $filter: SkuFilter) {
    productLinesList(
      condition: { brandId: $brandId }
      orderBy: CREATED_AT_ASC
    ) {
      id
      label: name
      options: skusList(orderBy: CREATED_AT_ASC, filter: $filter) {
        ...SelectSkuMenuSku
      }
    }
  }
`);

const valueQuery = gql(/* GraphQL */ `
  query SelectSkuMenuValue($ids: [UUID!]!) {
    skusList(filter: { id: { in: $ids } }) {
      ...SelectSkuMenuSkuValue
    }
  }
`);

type SkuOptionValue = FragmentType<typeof valueFragment>;

export type SkuOption = FragmentType<typeof skuFragment> | SkuOptionValue;

function getIsSkuOptionValue(option: SkuOption): option is SkuOptionValue {
  return "valueName" in option;
}

type OptionsData<Option extends SkuOption = SkuOption> = {
  readonly productLinesList: readonly GroupBase<Option>[] | null;
};

function getSkuData(option: SkuOption) {
  return getFragmentData(
    skuFragment,
    getIsSkuOptionValue(option)
      ? getFragmentData(valueFragment, option)
      : option,
  );
}

function getOptionValue(option: SkuOption) {
  return getSkuData(option).id;
}

function getOptionLabel(option: SkuOption) {
  if (getIsSkuOptionValue(option)) {
    const valueFragmentData = getFragmentData(valueFragment, option);
    if (valueFragmentData.valueName) return valueFragmentData.valueName;
    const optionFragment = getFragmentData(skuFragment, valueFragmentData);
    return optionFragment.fullNameShort ?? optionFragment.name ?? " ";
  }
  return getFragmentData(skuFragment, option).name ?? " ";
}

export type BaseSelectSkuMenuProps<
  Option extends SkuOption = SkuOption,
  IsMulti extends boolean = false,
  TData extends OptionsData<Option> = OptionsData<Option>,
  TVariables extends OperationVariables = OperationVariables,
> = StateManagerProps<Option, IsMulti> & {
  excludedIds?: UUID[];
  lazyQuery: LazyQueryResultTuple<TData, TVariables>;
  themingProps?: SelectThemingProps;
};

function BaseSelectSkuMenuInner<
  Option extends SkuOption = SkuOption,
  IsMulti extends boolean = false,
  TData extends OptionsData<Option> = OptionsData<Option>,
  TVariables extends OperationVariables = OperationVariables,
>(
  {
    components,
    excludedIds,
    isLoading: isLoadingProp = false,
    lazyQuery,
    styles,
    themingProps,
    ...rest
  }: BaseSelectSkuMenuProps<Option, IsMulti, TData, TVariables>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti>>,
) {
  const styleProps = useSelectStyles<Option, IsMulti>(styles, themingProps);

  const SkuOptionComponent = useMemo(() => {
    // eslint-disable-next-line react/no-unstable-nested-components
    function SkuOption({
      children,
      innerProps,
      ...optionProps
    }: OptionProps<Option, IsMulti>) {
      const { isSelected, data } = optionProps;
      const sku = getSkuData(data);
      return (
        <baseComponents.Option
          {...optionProps}
          innerProps={{ ...innerProps, "aria-selected": isSelected }}
          isSelected={isSelected}
        >
          <Flex
            alignItems="baseline"
            gap={2}
            data-is-archived={sku.isArchived || undefined}
            sx={{ "&[data-is-archived=true]": { opacity: 0.65 } }}
          >
            <Box>{children}</Box>
            {sku.isArchived && <ArchivedBadge ml="auto" />}
          </Flex>

          {!!sku.internalSkuId && (
            <Box opacity={0.75} fontWeight={300} fontSize=".8em">
              <Box
                as="span"
                fontFamily="mono"
                fontWeight="normal"
                opacity={0.6}
              >
                {sku.internalSkuId}
              </Box>
            </Box>
          )}
        </baseComponents.Option>
      );
    }

    return SkuOption;
  }, []);

  const getOptions = useCallback(
    (optionsData: TData) => {
      if (!optionsData?.productLinesList) return;

      if (!excludedIds?.length) return optionsData.productLinesList;

      return optionsData.productLinesList.reduce<GroupBase<Option>[]>(
        (acc, pl) => {
          const filteredSkus = pl.options.filter((sku) => {
            const skuData = getSkuData(sku);
            return !excludedIds.includes(skuData.id);
          });
          const numFiltered = filteredSkus.length;
          if (numFiltered) {
            if (numFiltered === pl.options.length) {
              acc.push(pl);
            } else {
              acc.push({ ...pl, options: filteredSkus });
            }
          }
          return acc;
        },
        [],
      );
    },
    [excludedIds],
  );

  const { options, isLoading, onMenuOpen } = useFetchOptions({
    getOptions,
    lazyQuery,
    menuIsOpen: rest.menuIsOpen,
  });

  return (
    <Select<Option, IsMulti>
      {...styleProps}
      {...{ ref, getOptionValue, getOptionLabel, options, onMenuOpen }}
      components={{
        ...styleProps.components,
        Option: SkuOptionComponent,
        ...components,
      }}
      isLoading={isLoading || isLoadingProp}
      noOptionsMessage={() => (
        <>
          No product lines or SKUs found.{" "}
          <Link
            href={PRODUCTS_INDEX}
            color="magenta.dark"
            whiteSpace="nowrap"
            _hover={{ color: "lightPink" }}
          >
            Add products ▸
          </Link>
        </>
      )}
      placeholder={`Select ${rest.isMulti ? "SKUs" : "a SKU"}…`}
      filterOption={createFilter({
        stringify: (option) =>
          `${option.label} ${option.value} ${
            getSkuData(option.data).internalSkuId || ""
          }`.trim(),
      })}
      {...rest}
    />
  );
}

export const BaseSelectSkuMenu = forwardRef(BaseSelectSkuMenuInner);

export type SelectSkuMenuProps<
  Option extends SkuOption = SkuOption,
  IsMulti extends boolean = false,
> = Omit<
  BaseSelectSkuMenuProps<Option, IsMulti>,
  "onChange" | "value" | "lazyQuery"
> &
  IdValueSelectProps<IsMulti> & {
    valueQueryOptions?: QueryHookOptions<
      SelectSkuMenuValueQuery,
      SelectSkuMenuValueQueryVariables
    >;
  } & {
    filter?: SkuFilter;
    isArchivedIncluded?: boolean;
  };

function SelectSkuMenuInner<IsMulti extends boolean = false>(
  {
    filter: filterProp,
    isArchivedIncluded = false,
    isLoading: isLoadingProp = false,
    isMulti,
    onChange,
    value: valueId,
    valueQueryOptions = {},
    ...rest
  }: SelectSkuMenuProps<SkuOption, IsMulti>,
  ref: ForwardedRef<SelectInstance<SkuOption, IsMulti>>,
) {
  const brandId = useCurrentBrandId();

  const filter = useOptionalCombineFilters(
    filterProp,
    useMemo<SkuFilter | undefined>(
      () =>
        isArchivedIncluded ? undefined : { isArchived: { equalTo: false } },
      [isArchivedIncluded],
    ),
  );

  const lazyQuery = useLazyQuery(optionsQuery, {
    variables: brandId ? { brandId, filter } : undefined,
  });

  const idValueProps = useIdValue<SkuOption, IsMulti, typeof valueQuery>({
    getOptionLabel,
    getOptionsFromData: (data) => getFragmentData(valueFragment, data.skusList),
    isMulti,
    labelKey: "name" as keyof SkuOption,
    onChange,
    query: valueQuery,
    queryOptions: valueQueryOptions,
    value: valueId,
  });

  return (
    <BaseSelectSkuMenu
      {...{ ref, isMulti, lazyQuery }}
      {...idValueProps}
      isLoading={idValueProps.isLoading || isLoadingProp}
      {...rest}
    />
  );
}

export default forwardRef(SelectSkuMenuInner);
